import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { isComingSoonId } from '@/lib/comingSoon';


export interface CartItem {
  id: string;
  name: string;
  price: string;
  duration: string;
  category: 'fitness' | 'elite' | 'football' | 'team' | 'service' | 'nutrition';
  packType?: string;
  quantity: number;
  priceId?: string; // Stripe price ID
  mode?: 'payment' | 'subscription';
  monthsCount?: number; // for subscriptions: total cycles before auto-cancel
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  itemCount: number;
}

const CART_STORAGE_KEY = 'valorform_cart';

const loadCart = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveCart = (items: CartItem[]) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore storage errors
  }
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = (item: CartItem) => {
    // Centralized guard: refuse to add Coming Soon items from anywhere.
    if (isComingSoonId(item.id)) {
      if (typeof window !== "undefined") {
        // Lightweight dynamic import to avoid pulling sonner into SSR/tests
        import("sonner").then(({ toast }) =>
          toast.error("This service isn't available yet — it's marked Coming Soon."),
        ).catch(() => {});
      }
      return;
    }
    setItems(prev => {
      const existingIndex = prev.findIndex(i => i.id === item.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + item.quantity
        };
        return updated;
      }
      return [...prev, item];
    });
  };


  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const clearCart = () => {
    setItems([]);
  };

  const itemCount = items.reduce((total, item) => total + item.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
